/**
 * Coupon enums, status derivation and cell formatting for the Coupons tab.
 *
 * Mirrors `CouponResponse` from saas-stripe-service (GET /coupon):
 * id, name, code, status, discountType, discountValue, expirationDate, durationMonths,
 * maxRedemptions, redemptionsCount, scope, appliesToProducts: [{ id, name }].
 * An empty `appliesToProducts` means the coupon discounts the entire invoice.
 */
import { endOfDay, isAfter, parseISO } from 'date-fns';
import { dateToFormat } from 'src/utils/date';
export const COUPON_DISCOUNT_TYPE = {
  PERCENT: 'Percent',
  FIXED: 'Fixed'
};
export const COUPON_SCOPE = {
  ACCOUNT: 'Account',
  PROPERTY: 'Property',
  BOTH: 'Both'
};

/**
 * The backend stores Draft or Active only; Expired and Depleted are derived here and always win
 * over the stored value.
 */
export const COUPON_STATUS = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  DEPLETED: 'Depleted',
  EXPIRED: 'Expired'
};

/** Statuses Ops can set by hand in the coupon modal. */
export const COUPON_EDITABLE_STATUSES = [COUPON_STATUS.ACTIVE, COUPON_STATUS.DRAFT];
export const COUPON_APPLIES_TO = {
  INVOICE: 'Entire invoice',
  PRODUCTS: 'Specific products'
};

/** Filter value for coupons that discount the whole invoice, i.e. every product. */
export const COUPON_PRODUCT_FILTER_ALL = 'All products';
export const DURATION_FOREVER = 'forever';
export const EXPIRATION_NONE = 'none';
export const EXPIRATION_CUSTOM = 'custom';
export const MAX_REDEMPTIONS_UNLIMITED = 'unlimited';
export const MAX_REDEMPTIONS_CUSTOM = 'custom';
export const COUPON_DURATION_OPTIONS = [{
  value: 1,
  label: '1 month'
}, {
  value: 3,
  label: '3 months'
}, {
  value: 6,
  label: '6 months'
}, {
  value: 12,
  label: '12 months'
}, {
  value: DURATION_FOREVER,
  label: 'Forever'
}];
export const COUPON_DISCOUNT_TYPE_OPTIONS = [{
  value: COUPON_DISCOUNT_TYPE.PERCENT,
  label: '% Percentage off'
}, {
  value: COUPON_DISCOUNT_TYPE.FIXED,
  label: '$ Fixed amount off'
}];

/** Relative periods, as in the design; the absolute date is computed under the field. */
export const COUPON_EXPIRATION_OPTIONS = [{
  value: EXPIRATION_NONE,
  label: 'No expiration'
}, {
  value: 1,
  label: '1 month'
}, {
  value: 3,
  label: '3 months'
}, {
  value: 6,
  label: '6 months'
}, {
  value: 12,
  label: '12 months'
}, {
  value: EXPIRATION_CUSTOM,
  label: 'Custom date'
}];
export const COUPON_MAX_REDEMPTION_OPTIONS = [{
  value: MAX_REDEMPTIONS_UNLIMITED,
  label: 'Unlimited'
}, {
  value: 25,
  label: '25'
}, {
  value: 50,
  label: '50'
}, {
  value: 100,
  label: '100'
}, {
  value: 250,
  label: '250'
}, {
  value: 500,
  label: '500'
}, {
  value: MAX_REDEMPTIONS_CUSTOM,
  label: 'Custom'
}];
const STATUS_PALETTE = {
  [COUPON_STATUS.DRAFT]: {
    bg: '#E3F2FD',
    color: '#1976D2'
  },
  [COUPON_STATUS.ACTIVE]: {
    bg: '#E8F5E9',
    color: '#2E7D32'
  },
  [COUPON_STATUS.DEPLETED]: {
    bg: '#FFF3E0',
    color: '#ED6C02'
  },
  [COUPON_STATUS.EXPIRED]: {
    bg: '#F5F5F5',
    color: '#9E9E9E'
  }
};
export function getCouponStatusTagSx(status) {
  const p = STATUS_PALETTE[status] || {
    bg: '#F5F5F5',
    color: '#616161'
  };
  return {
    backgroundColor: p.bg,
    color: p.color,
    fontWeight: 600,
    fontSize: '0.75rem',
    padding: '4px 10px',
    borderRadius: '4px'
  };
}

/**
 * The API sends a plain calendar day ("2026-07-16"). `new Date()` would read that as UTC midnight,
 * which is the evening before anywhere west of UTC; parseISO keeps it on the local calendar day.
 */
export function parseCouponDate(value) {
  if (!value) return null;
  const parsed = parseISO(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Expired beats depleted beats the stored Draft/Active value. */
export function getCouponStatus(coupon) {
  var _coupon$redemptionsCo;
  let now = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Date();
  const expires = parseCouponDate(coupon.expirationDate);
  // The expiration date is the last day the code can be redeemed, so it only expires once that
  // whole local day is behind us.
  if (expires && isAfter(now, endOfDay(expires))) return COUPON_STATUS.EXPIRED;
  if (coupon.maxRedemptions != null && ((_coupon$redemptionsCo = coupon.redemptionsCount) !== null && _coupon$redemptionsCo !== void 0 ? _coupon$redemptionsCo : 0) >= coupon.maxRedemptions) {
    return COUPON_STATUS.DEPLETED;
  }
  return coupon.status === COUPON_STATUS.DRAFT ? COUPON_STATUS.DRAFT : COUPON_STATUS.ACTIVE;
}
export function formatCouponDiscount(coupon) {
  const value = Number(coupon.discountValue);
  if (!Number.isFinite(value)) return '—';
  return coupon.discountType === COUPON_DISCOUNT_TYPE.FIXED ? "$".concat(value.toFixed(2), " off") : "".concat(Number(value.toFixed(2)), "% off");
}
export function formatCouponDuration(coupon) {
  const months = coupon.durationMonths;
  if (months == null) return 'Forever';
  return months === 1 ? '1 month' : "".concat(months, " months");
}

/** Expiration is optional — say so instead of leaving the cell blank. */
export function formatCouponExpiration(coupon) {
  if (!coupon.expirationDate) return 'No expiration';
  const formatted = dateToFormat(coupon.expirationDate);
  return formatted === 'N/A' ? 'No expiration' : formatted;
}

/** Max redemptions is optional — an uncapped coupon shows an infinity sign. */
export function formatCouponRedemptions(coupon) {
  var _coupon$redemptionsCo2;
  const count = (_coupon$redemptionsCo2 = coupon.redemptionsCount) !== null && _coupon$redemptionsCo2 !== void 0 ? _coupon$redemptionsCo2 : 0;
  return coupon.maxRedemptions != null ? "".concat(count, "/").concat(coupon.maxRedemptions) : "".concat(count, "/\u221E");
}
export function getCouponProductIds(coupon) {
  var _coupon$appliesToProd;
  return ((_coupon$appliesToProd = coupon.appliesToProducts) !== null && _coupon$appliesToProd !== void 0 ? _coupon$appliesToProd : []).map(product => product === null || product === void 0 ? void 0 : product.id).filter(Boolean);
}

/**
 * A product can be deleted while a coupon is still restricted to it. Such an entry must stay
 * visible — dropping it would read as a whole-invoice discount — and its name arrives null.
 */
export function formatCouponProductLabel(product) {
  var _product$name;
  const name = ((_product$name = product === null || product === void 0 ? void 0 : product.name) !== null && _product$name !== void 0 ? _product$name : '').trim();
  if (product !== null && product !== void 0 && product.isDeleted) return name ? "".concat(name, " (deleted)") : 'Deleted product';
  return name || 'Unnamed product';
}
export function getCouponProductLabels(coupon) {
  var _coupon$appliesToProd2;
  return ((_coupon$appliesToProd2 = coupon.appliesToProducts) !== null && _coupon$appliesToProd2 !== void 0 ? _coupon$appliesToProd2 : []).map(formatCouponProductLabel);
}

/** Filter keys for a coupon: its product ids, or "All products" when it discounts everything. */
export function getCouponProductFilterValues(coupon) {
  const ids = getCouponProductIds(coupon);
  return ids.length ? ids : [COUPON_PRODUCT_FILTER_ALL];
}
export function formatCouponAppliesTo(coupon) {
  const labels = getCouponProductLabels(coupon);
  return labels.length ? labels.join(', ') : COUPON_APPLIES_TO.INVOICE;
}